// @ts-nocheck
import { Badge, Box, Card, Group, SimpleGrid, Stack, Text, ThemeIcon, Title } from '@/components/ui';
import {
  IconShoppingCart,
  IconFolders,
  IconUsers,
  IconUserCircle,
  IconFolder,
  IconChartBar,
  IconMessage,
  IconCalendar,
  IconReceipt,
  IconHeadset,
  IconApps,
} from '@tabler/icons-react';
// @ts-nocheck
import { useNavigate } from 'react-router-dom';

const apps = [
  {
    icon: IconShoppingCart,
    title: 'E-Commerce',
    description: 'Complete online store admin with sales analytics, product catalog, orders, and customer management',
    path: '/demo/apps/ecommerce',
    color: 'blue',
    pages: ['Dashboard', 'Products', 'Orders', 'Customers'],
  },
  {
    icon: IconFolders,
    title: 'Project Management',
    description: 'Project tracking with Kanban boards, timelines, team collaboration, and progress monitoring',
    path: '/demo/apps/project-management',
    color: 'violet',
    pages: ['Dashboard', 'Projects', 'Board', 'Team'],
  },
  {
    icon: IconUsers,
    title: 'CRM',
    description: 'Customer relationship management with deal pipeline, contacts, and activity tracking',
    path: '/demo/apps/crm',
    color: 'green',
    pages: ['Dashboard', 'Pipeline', 'Contacts', 'Activities'],
  },
  {
    icon: IconUserCircle,
    title: 'HR Portal',
    description: 'Human resources portal with employee directory, org chart, and leave management',
    path: '/demo/apps/hr-portal',
    color: 'orange',
    pages: ['Dashboard', 'Directory', 'Organization', 'Leave'],
  },
  {
    icon: IconFolder,
    title: 'File Manager',
    description: 'Cloud storage interface with file browser, uploads, sharing, and document previews',
    path: '/demo/apps/file-manager',
    color: 'cyan',
    pages: ['Files', 'Recent', 'Shared', 'Trash'],
  },
  {
    icon: IconChartBar,
    title: 'Analytics',
    description: 'Data visualization dashboard with KPIs, charts, report builder, and real-time metrics',
    path: '/demo/apps/analytics',
    color: 'pink',
    pages: ['Overview', 'Reports', 'Realtime', 'Exports'],
  },
  {
    icon: IconMessage,
    title: 'Chat',
    description: 'Messaging application with conversations, contacts, group chats, and notifications',
    path: '/demo/apps/chat',
    color: 'indigo',
    pages: ['Messages', 'Contacts', 'Groups', 'Settings'],
  },
  {
    icon: IconCalendar,
    title: 'Calendar',
    description: 'Scheduling app with calendar views, meeting scheduler, and availability management',
    path: '/demo/apps/calendar',
    color: 'teal',
    pages: ['Calendar', 'Schedule', 'Availability', 'Events'],
  },
  {
    icon: IconReceipt,
    title: 'Invoicing',
    description: 'Billing system with invoice creation, payment tracking, and financial reports',
    path: '/demo/apps/invoicing',
    color: 'yellow',
    pages: ['Dashboard', 'Invoices', 'Create', 'Payments'],
  },
  {
    icon: IconHeadset,
    title: 'Helpdesk',
    description: 'Support ticketing system with Kanban board, ticket management, and knowledge base',
    path: '/demo/apps/helpdesk',
    color: 'red',
    pages: ['Dashboard', 'Tickets', 'Board', 'Knowledge'],
  },
];

export default function AppsOverviewPage() {
  const navigate = useNavigate();

  return (
    <Stack gap="xl">
      {/* Header */}
      <Box>
        <Text gradient gradientVariant="primary" size="xl" fw={700}>
          Demo Applications
        </Text>
        <Text c="dimmed" size="sm">
          Explore complete application templates built with our component library - similar to ThemeForest templates
        </Text>
      </Box>

      {/* Welcome Card */}
      <Card variant="gradient" gradientVariant="secondary" hoverEffect="glow">
        <Stack gap="md">
          <Group gap="lg">
            <ThemeIcon size={60} radius="xl" variant="white" color="dark">
              <IconApps size={32} />
            </ThemeIcon>
            <Box style={{ flex: 1 }}>
              <Title order={3} c="white">
                10 Complete Application Templates
              </Title>
              <Text c="white" opacity={0.9}>
                Each application includes multiple pages with realistic mock data, interactive components,
                and production-ready layouts. Perfect for understanding how to build real-world applications.
              </Text>
            </Box>
          </Group>
        </Stack>
      </Card>

      {/* Apps Grid */}
      <Box>
        <Title order={3} mb="md">
          Browse Applications
        </Title>
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 2 }} spacing="lg">
          {apps.map((app) => (
            <Card
              key={app.path}
              shadow="sm"
              padding="lg"
              radius="md"
              withBorder
              style={{ cursor: 'pointer', transition: 'all 0.2s' }}
              onClick={() => navigate(app.path)}
            >
              <Stack gap="md">
                <Group justify="space-between">
                  <Group>
                    <ThemeIcon size={48} radius="md" variant="light" color={app.color}>
                      <app.icon size={24} />
                    </ThemeIcon>
                    <Box>
                      <Text fw={600} size="lg">
                        {app.title}
                      </Text>
                      <Badge size="xs" variant="light" color={app.color}>
                        {app.pages.length} pages
                      </Badge>
                    </Box>
                  </Group>
                </Group>
                <Text size="sm" c="dimmed">
                  {app.description}
                </Text>
                <Group gap="xs">
                  {app.pages.map((page) => (
                    <Badge key={page} size="sm" variant="outline" color="gray">
                      {page}
                    </Badge>
                  ))}
                </Group>
              </Stack>
            </Card>
          ))}
        </SimpleGrid>
      </Box>

      {/* Features */}
      <Card shadow="sm" padding="lg" radius="md" withBorder>
        <Stack gap="sm">
          <Title order={4}>What's Included</Title>
          <Text size="sm" c="dimmed">
            Each application template features:
          </Text>
          <Box component="ul" style={{ marginTop: 8, paddingLeft: 20 }}>
            <li>
              <Text size="sm">Multiple interconnected pages with navigation</Text>
            </li>
            <li>
              <Text size="sm">Realistic mock data that demonstrates component capabilities</Text>
            </li>
            <li>
              <Text size="sm">Interactive elements - modals, forms, filters, and actions</Text>
            </li>
            <li>
              <Text size="sm">Responsive layouts optimized for all screen sizes</Text>
            </li>
            <li>
              <Text size="sm">Theme-aware styling that works with all color schemes</Text>
            </li>
          </Box>
        </Stack>
      </Card>
    </Stack>
  );
}
